/**
 * Edit the company record: the company, its legal entities, worksites and job
 * descriptions. Saving rewrites the employer details on every linked case.
 */
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useState, type FC, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { usePracticeTeamMembers } from "@/hooks/usePracticeTeamMembers";

import { companyActions } from "../actions";
import type {
  Company,
  CompanyAddress,
  CompanyEntity,
  JobDescription,
  Worksite
} from "../types";

type Draft = {
  name: string;
  client_no: string;
  responsible_user_id: string | null;
  domains: string;
  notes: string;
  entities: Array<Partial<CompanyEntity>>;
  worksites: Array<Partial<Worksite>>;
  job_descriptions: Array<Partial<JobDescription>>;
};

const BLANK_ADDRESS: CompanyAddress = {
  street: "",
  city: "",
  state: "",
  zip_code: "",
  country: ""
};
const NOBODY = "__none__";

const draftOf = (c: Company): Draft => ({
  name: c.name,
  client_no: c.client_no,
  responsible_user_id: c.responsible_user_id,
  domains: c.domains.join(", "),
  notes: c.notes,
  entities: c.entities.map((e) => ({ ...e, address: { ...e.address } })),
  worksites: c.worksites.map((w) => ({ ...w, address: { ...w.address } })),
  job_descriptions: c.job_descriptions.map((j) => ({ ...j }))
});

const Section: FC<{
  title: string;
  action?: ReactNode;
  children: ReactNode;
}> = ({ title, action, children }) => (
  <div className="flex flex-col gap-3 border-b border-line-2 px-4 py-4 last:border-b-0">
    <div className="flex items-center">
      <h3 className="flex-1 text-base font-medium">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const Field: FC<{
  id: string;
  label: string;
  value: string | number | null | undefined;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
}> = ({ id, label, value, onChange, placeholder, mono }) => (
  <div className="flex min-w-0 flex-col gap-1.5">
    <Label htmlFor={id} className="text-sm">
      {label}
    </Label>
    <Input
      id={id}
      value={value ?? ""}
      placeholder={placeholder}
      className={mono ? "font-mono text-sm" : undefined}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  </div>
);

const AddressFields: FC<{
  id: string;
  value: Partial<CompanyAddress> | undefined;
  onChange: (a: CompanyAddress) => void;
}> = ({ id, value, onChange }) => {
  const a = { ...BLANK_ADDRESS, ...value };
  const set = (k: keyof CompanyAddress) => (v: string) =>
    onChange({ ...a, [k]: v });
  return (
    <div className="grid gap-2">
      <Field
        id={`${id}-street`}
        label="Street"
        value={a.street}
        onChange={set("street")}
      />
      <div className="grid grid-cols-[1fr_72px_96px] gap-2">
        <Field
          id={`${id}-city`}
          label="City"
          value={a.city}
          onChange={set("city")}
        />
        <Field
          id={`${id}-state`}
          label="State"
          value={a.state}
          onChange={set("state")}
        />
        <Field
          id={`${id}-zip`}
          label="ZIP"
          value={a.zip_code}
          onChange={set("zip_code")}
        />
      </div>
    </div>
  );
};

type Props = {
  company: Company;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Id of the input to focus on open, such as `ent-0-fein`. */
  field?: string;
};

export const CompanyProfileSheet: FC<Props> = ({
  company,
  open,
  onOpenChange,
  field
}) => {
  const [draft, setDraft] = useState<Draft>(() => draftOf(company));
  const [busy, setBusy] = useState(false);
  const team = usePracticeTeamMembers();

  useEffect(() => {
    if (open) setDraft(draftOf(company));
  }, [open, company]);

  const patch = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));
  const editAt = <K extends "entities" | "worksites" | "job_descriptions">(
    key: K,
    i: number,
    change: Partial<Draft[K][number]>
  ) =>
    patch(
      key,
      draft[key].map((item, j) =>
        j === i ? { ...item, ...change } : item
      ) as Draft[K]
    );
  const removeAt = <K extends "entities" | "worksites" | "job_descriptions">(
    key: K,
    i: number
  ) => patch(key, draft[key].filter((_, j) => j !== i) as Draft[K]);

  const defaultIndex = Math.max(
    0,
    draft.entities.findIndex((e) => e.is_default)
  );

  const save = async () => {
    setBusy(true);
    const saved = await companyActions.save(company, {
      name: draft.name,
      client_no: draft.client_no,
      responsible_user_id: draft.responsible_user_id,
      notes: draft.notes,
      domains: draft.domains
        .split(/[\s,]+/)
        .map((d) => d.trim())
        .filter(Boolean),
      entities: draft.entities.map((e, i) => ({
        ...e,
        is_default: i === defaultIndex,
        us_employees:
          e.us_employees === null ||
          e.us_employees === undefined ||
          `${e.us_employees}` === ""
            ? null
            : Number(e.us_employees),
        signatory: e.signatory?.name ? e.signatory : null
      })),
      worksites: draft.worksites,
      job_descriptions: draft.job_descriptions
    });
    setBusy(false);
    if (saved) onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="w-full gap-0 overflow-y-auto sm:max-w-xl"
        data-testid="company-profile-sheet"
        onOpenAutoFocus={(e) => {
          const input = field ? document.getElementById(field) : null;
          if (!input) return;
          e.preventDefault();
          input.focus();
          input.scrollIntoView({ block: "center" });
        }}
      >
        <SheetHeader className="border-b border-line-2">
          <SheetTitle>Edit {company.name}</SheetTitle>
          <SheetDescription>
            Petitioner fields on every employee case come from here. Saving
            updates{" "}
            {company.case_count === 1
              ? "1 linked case"
              : `${company.case_count} linked cases`}
            .
          </SheetDescription>
        </SheetHeader>

        <Section title="Company">
          <div className="grid grid-cols-2 gap-2">
            <Field
              id="co-name"
              label="Name"
              value={draft.name}
              onChange={(v) => patch("name", v)}
            />
            <Field
              id="co-client-no"
              label="Client no."
              value={draft.client_no}
              mono
              onChange={(v) => patch("client_no", v)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-sm">Responsible attorney</Label>
            <Select
              value={draft.responsible_user_id ?? NOBODY}
              onValueChange={(v) =>
                patch("responsible_user_id", v === NOBODY ? null : v)
              }
            >
              <SelectTrigger size="sm" aria-label="Responsible attorney">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NOBODY}>Nobody Yet</SelectItem>
                {team.options.map((o) => (
                  <SelectItem key={o.uid} value={o.uid}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Field
            id="co-domains"
            label="Email domains"
            value={draft.domains}
            placeholder="northwind.com"
            onChange={(v) => patch("domains", v)}
          />
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="co-notes" className="text-sm">
              Notes
            </Label>
            <Textarea
              id="co-notes"
              rows={2}
              value={draft.notes}
              onChange={(e) => patch("notes", e.currentTarget.value)}
            />
          </div>
        </Section>

        <Section
          title="Legal entities"
          action={
            <Button
              size="xs"
              variant="ghost"
              onClick={() =>
                patch("entities", [
                  ...draft.entities,
                  { legal_name: "", address: { ...BLANK_ADDRESS } }
                ])
              }
            >
              <Plus /> Entity
            </Button>
          }
        >
          <RadioGroup
            value={String(defaultIndex)}
            onValueChange={(v) =>
              patch(
                "entities",
                draft.entities.map((e, i) => ({
                  ...e,
                  is_default: i === Number(v)
                }))
              )
            }
            className="gap-4"
          >
            {draft.entities.map((e, i) => (
              <div
                key={e.id ?? `new-${i}`}
                className="flex flex-col gap-2 rounded-lg border border-line-2 p-3"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem
                    value={String(i)}
                    id={`ent-default-${i}`}
                    aria-label="Default entity"
                  />
                  <Label
                    htmlFor={`ent-default-${i}`}
                    className="flex-1 text-sm text-fg-3"
                  >
                    {i === defaultIndex
                      ? "Default for new cases"
                      : "Make default"}
                  </Label>
                  {draft.entities.length > 1 ? (
                    <Button
                      size="icon-xs"
                      variant="ghost"
                      aria-label="Remove entity"
                      onClick={() => removeAt("entities", i)}
                    >
                      <Trash2 />
                    </Button>
                  ) : null}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Field
                    id={`ent-${i}-legal`}
                    label="Legal name"
                    value={e.legal_name}
                    onChange={(v) => editAt("entities", i, { legal_name: v })}
                  />
                  <Field
                    id={`ent-${i}-dba`}
                    label="DBA"
                    value={e.dba}
                    onChange={(v) => editAt("entities", i, { dba: v })}
                  />
                  <Field
                    id={`ent-${i}-fein`}
                    label="FEIN"
                    value={e.fein}
                    mono
                    placeholder="12-3456789"
                    onChange={(v) => editAt("entities", i, { fein: v })}
                  />
                  <Field
                    id={`ent-${i}-naics`}
                    label="NAICS"
                    value={e.naics}
                    mono
                    onChange={(v) => editAt("entities", i, { naics: v })}
                  />
                  <Field
                    id={`ent-${i}-employees`}
                    label="US employees"
                    value={e.us_employees}
                    onChange={(v) =>
                      editAt("entities", i, {
                        us_employees:
                          v === "" ? null : Number(v.replace(/\D/g, ""))
                      })
                    }
                  />
                </div>
                <AddressFields
                  id={`ent-${i}`}
                  value={e.address}
                  onChange={(a) => editAt("entities", i, { address: a })}
                />
                <div className="grid grid-cols-3 gap-2">
                  {(["name", "title", "email"] as const).map((k) => (
                    <Field
                      key={k}
                      id={`ent-${i}-sig-${k}`}
                      label={
                        k === "name"
                          ? "Signatory"
                          : k === "title"
                          ? "Title"
                          : "Email"
                      }
                      value={e.signatory?.[k]}
                      onChange={(v) =>
                        editAt("entities", i, {
                          signatory: {
                            name: "",
                            title: "",
                            email: "",
                            ...e.signatory,
                            [k]: v
                          }
                        })
                      }
                    />
                  ))}
                </div>
              </div>
            ))}
          </RadioGroup>
        </Section>

        <Section
          title="Worksites"
          action={
            <Button
              size="xs"
              variant="ghost"
              onClick={() =>
                patch("worksites", [
                  ...draft.worksites,
                  { name: "", address: { ...BLANK_ADDRESS } }
                ])
              }
            >
              <Plus /> Worksite
            </Button>
          }
        >
          {draft.worksites.map((w, i) => (
            <div
              key={w.id ?? `new-${i}`}
              className="flex flex-col gap-2 rounded-lg border border-line-2 p-3"
            >
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Field
                    id={`ws-${i}-name`}
                    label="Name"
                    value={w.name}
                    onChange={(v) => editAt("worksites", i, { name: v })}
                  />
                </div>
                <span className="flex h-9 items-center gap-2">
                  <Switch
                    id={`ws-${i}-remote`}
                    checked={Boolean(w.remote)}
                    onCheckedChange={(on) =>
                      editAt("worksites", i, { remote: on })
                    }
                  />
                  <Label htmlFor={`ws-${i}-remote`} className="text-sm">
                    Remote
                  </Label>
                </span>
                <Button
                  size="icon-xs"
                  variant="ghost"
                  aria-label="Remove worksite"
                  onClick={() => removeAt("worksites", i)}
                >
                  <Trash2 />
                </Button>
              </div>
              {w.remote ? null : (
                <AddressFields
                  id={`ws-${i}`}
                  value={w.address}
                  onChange={(a) => editAt("worksites", i, { address: a })}
                />
              )}
            </div>
          ))}
          {!draft.worksites.length ? (
            <span className="text-sm text-fg-3">No worksites yet.</span>
          ) : null}
        </Section>

        <Section
          title="Job descriptions"
          action={
            <Button
              size="xs"
              variant="ghost"
              onClick={() =>
                patch("job_descriptions", [
                  ...draft.job_descriptions,
                  { title: "" }
                ])
              }
            >
              <Plus /> Job
            </Button>
          }
        >
          {draft.job_descriptions.map((j, i) => (
            <div
              key={j.id ?? `new-${i}`}
              className="flex flex-col gap-2 rounded-lg border border-line-2 p-3"
            >
              <div className="grid grid-cols-[1fr_110px_90px_auto] items-end gap-2">
                <Field
                  id={`job-${i}-title`}
                  label="Title"
                  value={j.title}
                  onChange={(v) => editAt("job_descriptions", i, { title: v })}
                />
                <Field
                  id={`job-${i}-soc`}
                  label="SOC code"
                  value={j.soc_code}
                  mono
                  onChange={(v) =>
                    editAt("job_descriptions", i, { soc_code: v })
                  }
                />
                <Field
                  id={`job-${i}-wage`}
                  label="Wage level"
                  value={j.wage_level}
                  onChange={(v) =>
                    editAt("job_descriptions", i, { wage_level: v })
                  }
                />
                <Button
                  size="icon-xs"
                  variant="ghost"
                  aria-label="Remove job"
                  onClick={() => removeAt("job_descriptions", i)}
                >
                  <Trash2 />
                </Button>
              </div>
              <Textarea
                aria-label="Duties"
                rows={2}
                placeholder="Duties"
                value={j.duties ?? ""}
                onChange={(e) =>
                  editAt("job_descriptions", i, {
                    duties: e.currentTarget.value
                  })
                }
              />
            </div>
          ))}
          {!draft.job_descriptions.length ? (
            <span className="text-sm text-fg-3">No job descriptions yet.</span>
          ) : null}
        </Section>

        <SheetFooter className="sticky bottom-0 flex-row justify-end border-t border-line-2 bg-surface">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={busy || !draft.name.trim()}
            onClick={() => void save()}
          >
            Save
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
